import {EventEmitter} from 'events';
import {IPlayer} from '../IPlayer';

const eventsToListen = {
  playing: (event, player) => ({src: player.src}),
  pause: (event, player) => ({src: player.src}),
  error: errorInterceptor,
  timeupdate: (event, player) => ({currentTime: player.currentTime, src: player.src}),
};

export const ERRORS = {
  NOT_ALLOWED_ERROR: 'NOT_ALLOWED_ERROR',
  NOT_SUPPORTED_ERROR: 'NOT_SUPPORTED_ERROR',
  PLAY_ABORTED_ERROR: 'PLAY_ABORTED_ERROR',
  MEDIA_ERR_ABORTED: 'MEDIA_ERR_ABORTED',
  MEDIA_ERR_NETWORK: 'MEDIA_ERR_NETWORK',
  MEDIA_ERR_DECODE: 'MEDIA_ERR_DECODE',
  MEDIA_ERR_SRC_NOT_SUPPORTED: 'MEDIA_ERR_SRC_NOT_SUPPORTED',
  UNKNOWN_ERROR: 'UNKNOWN_ERROR',
  UNKNOWN_PLAY_ERROR: 'UNKNOWN_ERROR',
};

export class LocalPlayer extends EventEmitter implements IPlayer {
  public inited: boolean;
  private audioElement: HTMLAudioElement;
  private __currentTrackDuration: number;

  constructor(audioElement = new Audio()) {
    super();
    this.inited = false;
    this.audioElement = audioElement;
    this.__currentTrackDuration = 0;
    this._initEventProxy();
  }

  get currentTime() {
    return this.audioElement.currentTime;
  }

  get src() {
    return this.audioElement.src;
  }

  set src(value) {
    const wasPlaying = !this.paused;
    this.audioElement.src = value;
    if (wasPlaying) {
      this.play();
    }
  }

  get currentTrackDuration() {
    return this.__currentTrackDuration;
  }

  set currentTrackDuration(value) {
    this.__currentTrackDuration = value;
  }

  get paused() {
    return this.audioElement.paused;
  }

  on(eventName: string, listener: () => void) {
    const events = [...Object.keys(eventsToListen), 'init', 'error', 'play-error'];
    if (!events.includes(eventName)) {
      throw new Error(`Event '${eventName}' is not supported`);
    }

    return super.on(eventName, listener);
  }

  _initEventProxy() {
    Object.keys(eventsToListen).forEach((eventName) => {
      const interceptor = eventsToListen[eventName];
      this.audioElement.addEventListener(eventName, (event) => {
        this.emit(eventName, interceptor(event, this));
      });
    });
  }

  init() {
    const {audioElement} = this;
    this.inited = true;
    this.emit('init');

    // because of mock-audio-element-adv
    if (audioElement instanceof HTMLElement) {
      document.body.appendChild(audioElement);
    }
    return Promise.resolve();
  }

  play() {
    this.__play().catch((ex) => {
      const knownErrorName = {
        NotAllowedError: ERRORS.NOT_ALLOWED_ERROR,
        NotSupportedError: ERRORS.NOT_SUPPORTED_ERROR,
        AbortError: ERRORS.PLAY_ABORTED_ERROR,
      }[ex.name];
      this.emit('play-error', knownErrorName || ERRORS.UNKNOWN_PLAY_ERROR, ex);
    });
  }

  pause() {
    this.audioElement.pause();
  }

  updateCurrentTime(value) {
    this.audioElement.currentTime = Math.min(value, this.currentTrackDuration);
    this.emit('timeupdate', {src: this.src, currentTime: this.audioElement.currentTime});
  }

  __play() {
    const res = this.audioElement.play();
    // @ts-expect-error
    if (res && res.then) {
      return res;
    }
    return new Promise((resolve) => {
      this.once('playing', resolve);
    });
  }
}

function errorInterceptor(event) {
  const el = event.target;
  /* codes
   1 = MEDIA_ERR_ABORTED - fetching process aborted by user
   2 = MEDIA_ERR_NETWORK - error occurred when downloading
   3 = MEDIA_ERR_DECODE - error occurred when decoding
   4 = MEDIA_ERR_SRC_NOT_SUPPORTED - audio/video not supported
   */
  if (el.src === location.href) {
    return;
  }
  const code = el.error && el.error.code;
  if (!code) {
    return ERRORS.UNKNOWN_ERROR;
  }
  return (
    {
      1: ERRORS.MEDIA_ERR_ABORTED,
      2: ERRORS.MEDIA_ERR_NETWORK,
      3: ERRORS.MEDIA_ERR_DECODE,
      4: ERRORS.MEDIA_ERR_SRC_NOT_SUPPORTED,
    }[code] || ERRORS.UNKNOWN_ERROR
  );
}
